<template>
  <div class="client-dash">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ greeting }}, {{ userName }}</h1>
        <p class="page-sub">{{ t('portal.dashSub') }}</p>
      </div>
      <router-link to="/portal/requests" class="btn btn-primary">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        {{ t('portal.requestWork') }}
      </router-link>
    </div>

    <div v-if="loading" class="spinner"></div>

    <template v-else>
      <!-- Client KPIs (no dev triage language) -->
      <div class="stat-strip">
        <div class="stat">
          <div class="stat-value">{{ kpi.projects }}</div>
          <div class="stat-label">{{ t('portal.activeProjects') }}</div>
        </div>
        <div class="stat">
          <div class="stat-value">{{ kpi.openRequests }}</div>
          <div class="stat-label">{{ t('portal.openRequests') }}</div>
        </div>
        <div class="stat">
          <div class="stat-value">{{ kpi.hours }}</div>
          <div class="stat-label">{{ t('portal.hoursTracked') }}</div>
        </div>
        <div class="stat">
          <div class="stat-value">{{ kpi.billing }}</div>
          <div class="stat-label">{{ t('portal.estBilling') }}</div>
        </div>
      </div>

      <!-- Your projects -->
      <div class="section-header">
        <h2 class="section-title">{{ t('portal.yourProjects') }}</h2>
        <router-link to="/portal/projects" class="btn btn-ghost btn-sm">{{ t('common.viewAll') }} →</router-link>
      </div>
      <div v-if="projects.length === 0" class="empty-state"><p>{{ t('portal.noProjects') }}</p></div>
      <div v-else class="proj-grid">
        <router-link v-for="p in projects" :key="p.id" :to="'/portal/projects/' + p.id" class="proj-card">
          <span class="proj-dot" :style="{ background: p.color }"></span>
          <div class="proj-body">
            <div class="proj-name">{{ p.name }}</div>
            <div class="proj-meta">{{ p.task_count }} {{ t('portal.openTasks') }} · {{ priceLabel(p) }}</div>
          </div>
        </router-link>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { fmtMoney, projectTotal } from '@/utils/billing'

export default {
  name: 'ClientDashboard',
  setup() {
    const { currentUser } = useAuth()
    const { t } = useI18n()
    const projects = ref([])
    const tasks    = ref([])
    const requests = ref([])
    const loading  = ref(true)

    const userName = computed(() => currentUser()?.name?.split(' ')[0] || '')
    const greeting = computed(() => {
      const h = new Date().getHours()
      return h < 12 ? t('dashboard.morning') : h < 17 ? t('dashboard.afternoon') : t('dashboard.evening')
    })

    const kpi = computed(() => {
      const secs = tasks.value.reduce((s, tk) => s + (tk.total_seconds || 0), 0)
      const bill = projects.value.reduce((s, p) => s + (projectTotal(p) || 0), 0)
      const cur  = projects.value.find(p => p.currency)?.currency || 'ILS'
      return {
        projects: projects.value.length,
        openRequests: requests.value.filter(r => r.status === 'pending').length,
        hours: Math.round((secs / 3600) * 10) / 10,
        billing: fmtMoney(bill, cur),
      }
    })

    const priceLabel = (p) => {
      if (!p.pricing_type || p.pricing_type === 'none') return t('portal.noPricing')
      const b = p.billable || {}
      return fmtMoney(projectTotal(p), b.currency || p.currency || 'ILS')
    }

    async function load() {
      loading.value = true
      try {
        const [pr, tr, rr] = await Promise.all([api.getProjects(), api.getTasks(), api.getRequests()])
        projects.value = pr.data || []
        tasks.value    = tr.data || []
        requests.value = rr.data || []
      } catch {} finally { loading.value = false }
    }

    let off1 = () => {}, off2 = () => {}
    onMounted(() => {
      load()
      const r = debounce(load, 400)
      off1 = onRealtime('task.changed', r)
      off2 = onRealtime('request.changed', r)
    })
    onUnmounted(() => { off1(); off2() })

    return { t, userName, greeting, kpi, projects, loading, priceLabel }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.stat-strip {
  display: grid; grid-template-columns: repeat(4, 1fr);
  border: 1px solid var(--border); border-radius: var(--radius);
  background: var(--surface); overflow: hidden; margin-bottom: 30px;
}
.stat { padding: 16px 20px; }
.stat + .stat { border-inline-start: 1px solid var(--border); }
.stat-value { font-variant-numeric: tabular-nums; font-size: var(--fs-h1); font-weight: 600; line-height: 1; letter-spacing: -.02em; }
.stat-label { font-size: 11px; color: var(--muted); margin-top: 7px; }
.section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.section-title { font-size: 16px; font-weight: 600; }
.proj-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }
.proj-card {
  display: flex; align-items: center; gap: 12px;
  background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius);
  padding: 14px 16px; text-decoration: none; color: var(--text);
  transition: border-color .12s, background .12s;
}
.proj-card:hover { border-color: var(--accent); background: var(--accent-wash); }
.proj-dot { width: 12px; height: 12px; border-radius: 4px; flex-shrink: 0; }
.proj-name { font-size: 14px; font-weight: 600; }
.proj-meta { font-size: 12px; color: var(--muted); margin-top: 1px; }
@media (max-width: 900px) {
  .stat-strip { grid-template-columns: repeat(2, 1fr); }
  .stat:nth-child(3) { border-inline-start: none; }
}
</style>
